import { useState, useEffect } from "react";
import { Row, Form, List, Button, InputNumber, Typography, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import Icon from "@ant-design/icons";
import { Icons } from "../../assets/icons";
import { InvoiceItem } from "../../repository/data.default";
import {
  addInvoiceItem,
  deleteInvoiceItem,
} from "../../repository/data.repository";
import { getProblemMessage } from "../../utils/http";

export interface InvoiceItemsManagmentProps {
  readonly invoiceItems: InvoiceItem[];
  readonly invoiceUid?: string;
  readonly loadItems: () => void;
  readonly setError: (message: string) => void;
}

export interface ItemsForm {
  readonly description: string;
  readonly quantity: number;
  readonly unitPrice: number;
  readonly vatPercentage: number;
}

function InvoiceItemsManagment(props: InvoiceItemsManagmentProps) {
  const [invoiceItemsForm] = Form.useForm();
  const itemValues = Form.useWatch<ItemsForm>([], invoiceItemsForm);
  const [listLoading, setListLoading] = useState(false);
  const [valid, setValid] = useState(false);

  // Lifecycle

  useEffect(() => {
    if (itemValues?.description) {
      setValid(true);
    } else {
      setValid(false);
    }
  }, [itemValues?.description]);

  // Methods

  function onAddItem() {
    const { description, quantity, unitPrice, vatPercentage } = itemValues;
    props.setError("");
    setListLoading(true);
    addInvoiceItem({
      invoiceUid: props.invoiceUid!,
      description,
      quantity,
      unitPrice,
      vatPercentage,
    })
      .then(() => {
        props.loadItems();
        setListLoading(false);
        invoiceItemsForm.resetFields();
      })
      .catch((err) => {
        setListLoading(false);
        props.setError(getProblemMessage(err));
      });
  }

  function onRemoveItem(item: InvoiceItem) {
    const { uid } = item;
    setListLoading(true);
    deleteInvoiceItem(uid)
      .then(() => {
        props.loadItems();
        setListLoading(false);
      })
      .catch((err) => {
        setListLoading(false);
        props.setError(getProblemMessage(err));
      });
  }

  return (
    <Row className="flex flex-col rounded-md border-2 border-solid border-gray-300 p-2">
      {/* Title */}
      <Typography.Title level={4} className="m-0 mb-6 self-center">
        Invoice Items
      </Typography.Title>
      {/* Items Managment Form */}
      <Form
        form={invoiceItemsForm}
        autoComplete="off"
        className="flex justify-between"
      >
        <Form.Item
          className="m-0 w-1/4 p-0"
          layout="horizontal"
          label={"Description"}
          name={"description"}
          rules={[
            {
              required: true,
              message: "Description is required",
            },
          ]}
        >
          <TextArea rows={1} />
        </Form.Item>
        <Form.Item
          className="m-0 w-1/5 p-0"
          label={"Quantity"}
          name={"quantity"}
          rules={[
            {
              required: false,
            },
            {
              validator: (_, value) => {
                const numValue = Number(value);
                if (!Number.isInteger(numValue)) {
                  return Promise.reject(
                    new Error("Only integer values are allowed"),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber addonAfter="pcs" />
        </Form.Item>
        <Form.Item
          className="m-0 w-1/5 p-0"
          label={"Unit Price"}
          name={"unitPrice"}
        >
          <InputNumber addonAfter="€" />
        </Form.Item>
        <Form.Item
          className="m-0 w-1/5 p-0"
          label={"Vat Percentage"}
          name={"vatPercentage"}
        >
          <InputNumber addonAfter="%" />
        </Form.Item>
        <Form.Item>
          <Button
            disabled={!valid}
            type="primary"
            htmlType="submit"
            onClick={onAddItem}
          >
            Add Item
          </Button>
        </Form.Item>
      </Form>
      {/* Items List */}
      <List
        className="h-60 overflow-auto"
        dataSource={props.invoiceItems}
        renderItem={(item, index) => (
          <List.Item
            className="flex flex-row justify-between"
            actions={[
              <Button
                type="text"
                danger
                onClick={() => onRemoveItem(item)}
                icon={
                  <Icon
                    className="-ml-0.5 text-xl"
                    component={Icons.CloseIcon}
                  />
                }
              ></Button>,
            ]}
          >
            <Space className="flex basis-1/5 items-center">
              <Typography.Text>
                {index + 1}. {item.description}
              </Typography.Text>
            </Space>
            <Space className="basis-1/5">
              <Typography.Text> {item.quantity}</Typography.Text>
            </Space>
            <Space className="basis-1/5">
              <Typography.Text>{item.unitPrice.toFixed(2)} €</Typography.Text>
            </Space>
            <Space className="basis-1/5">
              <Typography.Text>{item.vatPercentage} %</Typography.Text>
            </Space>
          </List.Item>
        )}
        loading={listLoading}
      ></List>
    </Row>
  );
}

export default InvoiceItemsManagment;
